/**
 * Development logging utilities
 * 
 * Provides conditional logging that only outputs in development builds
 * to avoid cluttering production console logs.
 */

/**
 * Checks if we're in development mode
 */
const isDevelopment = (): boolean => {
  return process.env.NODE_ENV === 'development';
};

/**
 * Development logger with different log levels
 */
export const devLogger = {
  /**
   * Logs cache invalidation events
   */
  cacheInvalidation: (operation: string, details: {
    queriesInvalidated?: number;
    patterns?: string[];
    targetKeys?: readonly unknown[][];
    duration?: number;
  }): void => {
    if (!isDevelopment()) return;

    const { queriesInvalidated, patterns, targetKeys, duration } = details;
    
    console.group(`🔄 Cache Invalidation: ${operation}`);
    
    if (queriesInvalidated !== undefined) {
      console.log(`📊 Queries invalidated: ${queriesInvalidated}`);
    }
    
    if (patterns && patterns.length > 0) {
      console.log(`🎯 Patterns matched:`, patterns);
    }
    
    if (targetKeys && targetKeys.length > 0) {
      console.log(`🔑 Specific keys invalidated:`);
      targetKeys.forEach((key, index) => {
        const formattedKey = key.map(k => 
          typeof k === 'object' ? JSON.stringify(k) : String(k)
        ).join(' → ');
        console.log(`  ${index + 1}. ${formattedKey}`);
      });
    }
    
    if (duration !== undefined) {
      console.log(`⏱️ Duration: ${duration.toFixed(2)}ms`);
    }
    
    console.groupEnd();
  },

  /**
   * Logs query key matching events
   */
  queryKeyMatch: (queryKey: readonly unknown[], matched: boolean, reason?: string): void => {
    if (!isDevelopment()) return;

    const formattedKey = queryKey.map(k => 
      typeof k === 'object' ? JSON.stringify(k) : String(k)
    ).join(' → ');

    const icon = matched ? '✅' : '❌';
    const action = matched ? 'MATCHED' : 'SKIPPED';
    
    console.log(`${icon} ${action}: ${formattedKey}${reason ? ` (${reason})` : ''}`);
  },

  /**
   * Logs performance metrics for cache operations
   */
  performance: (operation: string, startTime: number, endTime: number, metadata?: Record<string, unknown>): void => {
    if (!isDevelopment()) return;

    const duration = endTime - startTime;
    const formattedDuration = duration < 1 ? `${duration.toFixed(2)}ms` : `${duration.toFixed(0)}ms`;
    
    console.log(`⚡ ${operation}: ${formattedDuration}`, metadata || '');
  },

  /**
   * Logs warning messages in development
   */
  warn: (message: string, data?: unknown): void => {
    if (!isDevelopment()) return;
    console.warn(`⚠️ [Dev Warning] ${message}`, data || '');
  },

  /**
   * Logs debug information in development
   */
  debug: (message: string, data?: unknown): void => {
    if (!isDevelopment()) return;
    console.debug(`🐛 [Debug] ${message}`, data || '');
  },

  /**
   * Logs cache statistics
   */
  cacheStats: (stats: {
    totalQueries?: number;
    invalidatedQueries?: number;
    cacheHitRate?: number;
    operation?: string;
  }): void => {
    if (!isDevelopment()) return;

    const { totalQueries, invalidatedQueries, cacheHitRate, operation } = stats;
    
    console.log(`📈 Cache Stats${operation ? ` (${operation})` : ''}:`);
    if (totalQueries !== undefined) {
      console.log(`  Total queries: ${totalQueries}`);
    }
    if (invalidatedQueries !== undefined) {
      console.log(`  Invalidated: ${invalidatedQueries}`);
    }
    if (cacheHitRate !== undefined) {
      console.log(`  Hit rate: ${(cacheHitRate * 100).toFixed(1)}%`);
    }
  },
} as const;

/**
 * Creates a scoped logger for a specific component or feature
 */
export const createScopedLogger = (scope: string) => ({
  cacheInvalidation: (operation: string, details: Parameters<typeof devLogger.cacheInvalidation>[1]) => 
    devLogger.cacheInvalidation(`${scope}:${operation}`, details),
  queryKeyMatch: (queryKey: readonly unknown[], matched: boolean, reason?: string) =>
    devLogger.queryKeyMatch(queryKey, matched, reason ? `${scope} - ${reason}` : scope),
  performance: (operation: string, startTime: number, endTime: number, metadata?: Record<string, unknown>) =>
    devLogger.performance(`${scope}:${operation}`, startTime, endTime, metadata),
  warn: (message: string, data?: unknown) =>
    devLogger.warn(`${scope}: ${message}`, data),
  debug: (message: string, data?: unknown) =>
    devLogger.debug(`${scope}: ${message}`, data),
  cacheStats: (stats: Parameters<typeof devLogger.cacheStats>[0]) =>
    devLogger.cacheStats({ ...stats, operation: scope }),
});